import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  Calendar as CalendarIcon,
  Download,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  Leaf,
  Users,
  Building2
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [comparative, setComparative] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [compRes, trendRes] = await Promise.all([
        axios.get(`${API}/analytics/comparative`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/monthly-trend?months=6`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setComparative(compRes.data);
      setMonthlyTrend(trendRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/objectives/organization/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_rse_${user.organization}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await axios.get(`${API}/objectives/organization/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_rse_${user.organization}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapport Excel téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export Excel");
    } finally {
      setExportingExcel(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "positive": return <TrendingUp className="w-5 h-5 text-chart-1" />;
      case "negative": return <TrendingDown className="w-5 h-5 text-destructive" />;
      default: return <Minus className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getChangeColor = (value) => {
    if (value > 0) return "text-chart-1";
    if (value < 0) return "text-destructive";
    return "text-muted-foreground";
  };

  const getChangeIcon = (value) => {
    if (value > 0) return <ArrowUpRight className="w-4 h-4" />;
    if (value < 0) return <ArrowDownRight className="w-4 h-4" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  // Prepare chart data
  const trendChartData = monthlyTrend?.trends?.map(t => ({
    name: t.month,
    "Score ESG": t.avg_score,
    "Environnement": t.esg_scores.environnement,
    "Social": t.esg_scores.social,
    "Gouvernance": t.esg_scores.gouvernance,
    "Décisions": t.decision_count
  })) || [];

  const comparisonData = comparative ? [
    {
      name: "Période précédente",
      score: comparative.period2.stats.avg_score,
      decisions: comparative.period2.stats.decision_count
    },
    {
      name: "Période actuelle",
      score: comparative.period1.stats.avg_score,
      decisions: comparative.period1.stats.decision_count
    }
  ] : [];

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Analytics RSE
            </h1>
            <p className="text-muted-foreground mt-1">
              Analyse comparative et tendances de votre performance ESG
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={handleExportExcel} 
              disabled={exportingExcel}
              className="rounded-full px-6"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportingExcel ? "Export..." : "Excel"}
            </Button>
            <Button 
              onClick={handleExportPdf} 
              disabled={exportingPdf}
              className="rounded-full px-6"
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportingPdf ? "Export..." : "PDF"}
            </Button>
          </div>
        </div>

        {/* Comparative Summary */}
        {comparative && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {/* Score ESG Change */}
              <Card className="stat-card card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      {getTrendIcon(comparative.trend)}
                    </div>
                    <Badge variant="outline" className={getChangeColor(comparative.changes.avg_score)}>
                      {comparative.changes.avg_score > 0 ? "+" : ""}{comparative.changes.avg_score} pts
                    </Badge>
                  </div>
                  <div className="stat-value text-foreground" data-testid="current-score">
                    {comparative.period1.stats.avg_score}%
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Score ESG actuel</p>
                </CardContent>
              </Card>

              {/* Environnement */}
              <Card className="stat-card card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-chart-1/10 flex items-center justify-center">
                      <Leaf className="w-6 h-6 text-chart-1" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${getChangeColor(comparative.changes.esg_scores.environnement)}`}>
                      {getChangeIcon(comparative.changes.esg_scores.environnement)}
                      {comparative.changes.esg_scores.environnement > 0 ? "+" : ""}{comparative.changes.esg_scores.environnement}
                    </div>
                  </div>
                  <div className="stat-value text-chart-1">
                    {comparative.period1.stats.esg_scores.environnement}%
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Environnement</p>
                </CardContent>
              </Card>

              {/* Social */}
              <Card className="stat-card card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-chart-3/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-chart-3" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${getChangeColor(comparative.changes.esg_scores.social)}`}>
                      {getChangeIcon(comparative.changes.esg_scores.social)}
                      {comparative.changes.esg_scores.social > 0 ? "+" : ""}{comparative.changes.esg_scores.social}
                    </div>
                  </div>
                  <div className="stat-value text-chart-3">
                    {comparative.period1.stats.esg_scores.social}%
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Social</p>
                </CardContent>
              </Card>

              {/* Gouvernance */}
              <Card className="stat-card card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-chart-5/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-chart-5" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${getChangeColor(comparative.changes.esg_scores.gouvernance)}`}>
                      {getChangeIcon(comparative.changes.esg_scores.gouvernance)}
                      {comparative.changes.esg_scores.gouvernance > 0 ? "+" : ""}{comparative.changes.esg_scores.gouvernance}
                    </div>
                  </div>
                  <div className="stat-value text-chart-5">
                    {comparative.period1.stats.esg_scores.gouvernance}%
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Gouvernance</p>
                </CardContent>
              </Card>
            </div>

            {/* Insights */}
            {comparative.insights && comparative.insights.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Lightbulb className="w-5 h-5 text-chart-4" />
                    Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {comparative.insights.map((insight, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 p-4 rounded-xl bg-muted/50"
                      >
                        <div className="w-8 h-8 rounded-full bg-chart-4/10 flex items-center justify-center flex-shrink-0">
                          <Lightbulb className="w-4 h-4 text-chart-4" />
                        </div>
                        <p className="text-sm text-foreground">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Charts */}
        <Tabs defaultValue="trend" className="space-y-6">
          <TabsList>
            <TabsTrigger value="trend" data-testid="tab-trend">
              <BarChart3 className="w-4 h-4 mr-2" />
              Tendance mensuelle
            </TabsTrigger>
            <TabsTrigger value="comparison" data-testid="tab-comparison">
              <TrendingUp className="w-4 h-4 mr-2" />
              Comparaison périodes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trend">
            <div className="grid gap-6">
              {/* ESG Score Trend */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Évolution du score ESG</CardTitle>
                  <CardDescription>Score global sur les 6 derniers mois</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    {trendChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trendChartData}>
                          <defs>
                            <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(140, 40%, 40%)" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="hsl(140, 40%, 40%)" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                          <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="Score ESG" 
                            stroke="hsl(140, 40%, 40%)" 
                            fillOpacity={1} 
                            fill="url(#colorScore)" 
                            strokeWidth={3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Pas assez de données pour afficher la tendance
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ESG Categories Trend */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Évolution par catégorie ESG</CardTitle>
                  <CardDescription>Environnement, Social et Gouvernance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    {trendChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                          <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="Environnement" stroke="hsl(140, 40%, 40%)" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="Social" stroke="hsl(25, 60%, 55%)" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="Gouvernance" stroke="hsl(200, 50%, 45%)" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Pas assez de données pour afficher la tendance
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="comparison">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Score Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Comparaison des scores</CardTitle>
                  <CardDescription>Période actuelle vs période précédente</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    {comparisonData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                          <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="score" fill="hsl(140, 40%, 40%)" radius={[4, 4, 0, 0]} name="Score ESG" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Pas assez de données
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Decisions Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Volume de décisions</CardTitle>
                  <CardDescription>Nombre de décisions analysées</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    {comparisonData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                          <YAxis stroke="hsl(var(--muted-foreground))" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="decisions" fill="hsl(25, 60%, 55%)" radius={[4, 4, 0, 0]} name="Décisions" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Pas assez de données
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Comparison Table */}
              {comparative && (
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Manrope' }}>Détail comparatif</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Period 1 */}
                      <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                        <h4 className="font-semibold text-foreground mb-4">{comparative.period1.label}</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Score ESG</span>
                            <span className="font-bold text-foreground">{comparative.period1.stats.avg_score}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Décisions</span>
                            <span className="font-bold text-foreground">{comparative.period1.stats.decision_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Stratégiques</span>
                            <span className="font-bold text-foreground">{comparative.period1.stats.by_type.strategique}</span>
                          </div>
                        </div>
                      </div>

                      {/* Changes */}
                      <div className="p-4 rounded-xl bg-muted/50 flex flex-col items-center justify-center">
                        <h4 className="font-semibold text-foreground mb-4">Variation</h4>
                        <div className={`text-4xl font-bold ${getChangeColor(comparative.changes.avg_score)}`}>
                          {comparative.changes.avg_score > 0 ? "+" : ""}{comparative.changes.avg_score}
                        </div>
                        <span className="text-sm text-muted-foreground mt-2">points ESG</span>
                        <div className="mt-4">
                          {getTrendIcon(comparative.trend)}
                        </div>
                      </div>

                      {/* Period 2 */}
                      <div className="p-4 rounded-xl bg-muted/30 border border-border">
                        <h4 className="font-semibold text-foreground mb-4">{comparative.period2.label}</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Score ESG</span>
                            <span className="font-bold text-foreground">{comparative.period2.stats.avg_score}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Décisions</span>
                            <span className="font-bold text-foreground">{comparative.period2.stats.decision_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Stratégiques</span>
                            <span className="font-bold text-foreground">{comparative.period2.stats.by_type.strategique}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
